import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Animated, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const RegistryStatistics = ({ 
  dashboardStats, 
  formatCurrency, 
  cardStyle, 
  isMobile,
  registryItems = [],
  navigation,
  currentUserData
}) => {
  const colors = {
    primary: '#4c956c',
    secondary: '#6366f1',
    warning: '#f59e0b',
    danger: '#ef4444',
    success: '#22c55e',
    info: '#3b82f6',
    cream: '#fefde2',
    creamLight: 'rgba(254, 253, 226, 0.95)',
    text: '#1a3a2a',
    textMuted: '#5a6b63',
    white: '#ffffff'
  };

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const buttonScale = useRef(new Animated.Value(1)).current;
  const statAnims = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
    new Animated.Value(0)
  ]).current;

  // State for expanded sections
  const [expandedStat, setExpandedStat] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Entrance animations
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 8,
        tension: 40,
        useNativeDriver: true
      })
    ]).start();

    // Staggered stat card animations
    statAnims.forEach((anim, index) => {
      Animated.timing(anim, {
        toValue: 1,
        duration: 300,
        delay: 150 + (index * 100),
        useNativeDriver: true
      }).start();
    });
  }, []);

  // Animate progress bar when stats change
  useEffect(() => {
    const targetProgress = dashboardStats.totalItems > 0 
      ? (dashboardStats.purchasedItems / dashboardStats.totalItems) 
      : 0;
    
    Animated.timing(progressAnim, {
      toValue: targetProgress,
      duration: 800,
      delay: 400,
      useNativeDriver: false
    }).start();
  }, [dashboardStats.purchasedItems, dashboardStats.totalItems]);

  // Get items for each stat category
  const purchasedItemsList = registryItems.filter(item => item.purchased);
  const unpurchasedItemsList = registryItems.filter(item => !item.purchased);

  const stats = [
    {
      id: 'total_items',
      value: dashboardStats.totalItems,
      label: 'Total Listed',
      icon: 'layers-outline',
      color: colors.info,
      bgColor: 'rgba(59, 130, 246, 0.1)',
      items: registryItems
    },
    {
      id: 'purchased_items',
      value: dashboardStats.purchasedItems,
      label: 'Purchased',
      icon: 'checkmark-circle-outline',
      color: colors.success,
      bgColor: 'rgba(34, 197, 94, 0.1)',
      items: purchasedItemsList
    },
    {
      id: 'remaining_items',
      value: dashboardStats.remainingItems,
      label: 'Unpurchased',
      icon: 'gift-outline',
      color: colors.warning,
      bgColor: 'rgba(245, 158, 11, 0.1)',
      items: unpurchasedItemsList
    }
  ];

  // Calculate progress
  const progressPercent = dashboardStats.totalItems > 0 
    ? Math.round((dashboardStats.purchasedItems / dashboardStats.totalItems) * 100) 
    : 0;

  const toggleExpand = (statId) => {
    setExpandedStat(expandedStat === statId ? null : statId);
  };

  // Button hover and press animations
  const handleMouseEnter = () => {
    setIsButtonHovered(true);
    Animated.spring(buttonScale, {
      toValue: 1.03,
      friction: 8,
      useNativeDriver: true
    }).start();
  };

  const handleMouseLeave = () => {
    setIsButtonHovered(false);
    Animated.spring(buttonScale, {
      toValue: 1,
      friction: 8,
      useNativeDriver: true
    }).start();
  };

  const handlePressIn = () => {
    Animated.spring(buttonScale, {
      toValue: 0.95,
      friction: 8,
      useNativeDriver: true
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(buttonScale, {
      toValue: isButtonHovered ? 1.03 : 1,
      friction: 8,
      useNativeDriver: true
    }).start();
  };

  const handleUpdateRegistry = () => {
    if (navigation && currentUserData?.uid) {
      // Navigate to user's own registry page with param to open catalogue
      navigation.navigate('Registry', { 
        username: currentUserData.uid,
        openCatalogue: true 
      });
    } else if (navigation) {
      navigation.navigate('Registry', { openCatalogue: true });
    } else {
      // Fallback to URL navigation
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mytassel.com';
      Linking.openURL(`${baseUrl}/registry?openCatalogue=true`);
    }
  };

  // Web-specific hover styles
  const webButtonStyle = Platform.OS === 'web' ? {
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  } : {};

  return (
    <Animated.View style={[
      cardStyle, 
      { 
        padding: isMobile ? 20 : 28, 
        overflow: 'hidden', 
        position: 'relative',
        opacity: fadeAnim,
        transform: [
          { translateY: slideAnim },
          { scale: scaleAnim }
        ]
      }
    ]}>
      {/* Decorative Bubbles with subtle animation */}
      <Animated.View style={{
        position: 'absolute',
        top: -25,
        right: -25,
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: 'rgba(76, 149, 108, 0.06)',
        opacity: fadeAnim
      }} />
      <Animated.View style={{
        position: 'absolute',
        bottom: -15,
        left: 30,
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        opacity: fadeAnim
      }} />
      
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <View>
          <Text style={{
            fontSize: isMobile ? 18 : 22,
            fontWeight: '700',
            color: colors.text,
            fontFamily: 'Bodoni72Smallcaps'
          }}>
            Registry Stats
          </Text>
          <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
            Your registry at a glance
          </Text>
        </View>
        
        {/* Progress Badge */}
        <Animated.View style={{
          backgroundColor: progressPercent >= 50 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
          paddingHorizontal: 14,
          paddingVertical: 6,
          borderRadius: 20,
          transform: [{ scale: scaleAnim }]
        }}>
          <Text style={{ 
            fontSize: 14, 
            fontWeight: '700', 
            color: progressPercent >= 50 ? colors.success : colors.warning 
          }}>
            {progressPercent}% Complete
          </Text>
        </Animated.View>
      </View>

      {/* Stats Grid - 3 columns with staggered animations */}
      <View style={{
        flexDirection: 'row',
        gap: 10,
        marginBottom: 16
      }}>
        {stats.map((stat, index) => {
          const isExpanded = expandedStat === stat.id;
          return (
            <Animated.View
              key={stat.id}
              style={{
                flex: 1,
                opacity: statAnims[index],
                transform: [{
                  translateY: statAnims[index].interpolate({
                    inputRange: [0, 1],
                    outputRange: [20, 0]
                  })
                }]
              }}
            >
              <TouchableOpacity 
                onPress={() => toggleExpand(stat.id)}
                activeOpacity={0.7}
                style={[{
                  backgroundColor: isExpanded ? stat.color : stat.bgColor,
                  borderRadius: 14,
                  padding: 12,
                  alignItems: 'center',
                  ...(Platform.OS === 'web' ? { transition: 'all 0.2s ease' } : {})
                }]}
              >
                <View style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: isExpanded ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.8)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 6
                }}>
                  <Ionicons name={stat.icon} size={16} color={isExpanded ? colors.white : stat.color} />
                </View>
                <Text style={{ 
                  fontSize: 22, 
                  fontWeight: 'bold', 
                  color: isExpanded ? colors.white : stat.color,
                  marginBottom: 2
                }}>
                  {stat.value}
                </Text>
                <Text style={{ 
                  fontSize: 11, 
                  color: isExpanded ? 'rgba(255,255,255,0.85)' : colors.textMuted, 
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  {stat.label}
                </Text>
                <Ionicons 
                  name={isExpanded ? 'chevron-up' : 'chevron-down'} 
                  size={14} 
                  color={isExpanded ? 'rgba(255,255,255,0.7)' : colors.textMuted} 
                  style={{ marginTop: 4 }}
                />
              </TouchableOpacity>
            </Animated.View>
          );
        })}
      </View>

      {/* Expandable Item List */}
      {expandedStat && (
        <Animated.View style={{
          backgroundColor: 'rgba(0,0,0,0.03)',
          borderRadius: 12,
          padding: 12,
          marginBottom: 16,
          maxHeight: 200
        }}>
          <ScrollView showsVerticalScrollIndicator={true} nestedScrollEnabled>
            {stats.find(s => s.id === expandedStat)?.items?.length > 0 ? (
              stats.find(s => s.id === expandedStat).items.map((item, idx) => (
                <View 
                  key={item.id || idx}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: 8,
                    borderBottomWidth: idx < stats.find(s => s.id === expandedStat).items.length - 1 ? 1 : 0,
                    borderBottomColor: 'rgba(0,0,0,0.06)'
                  }}
                >
                  <View style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: item.purchased ? colors.success : colors.warning,
                    marginRight: 10
                  }} />
                  <Text style={{ flex: 1, fontSize: 13, color: colors.text }} numberOfLines={1}>
                    {item.name || item.title || 'Item'}
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted }}>
                    {item.price || formatCurrency(item.amount || 0)}
                  </Text>
                </View>
              ))
            ) : (
              <Text style={{ fontSize: 13, color: colors.textMuted, textAlign: 'center', paddingVertical: 16 }}>
                No items in this category
              </Text>
            )}
          </ScrollView>
        </Animated.View>
      )}

      {/* Progress Bar with Animation */}
      {dashboardStats.totalItems > 0 && (
        <View style={{ marginBottom: 16 }}>
          <View style={{
            height: 8,
            backgroundColor: 'rgba(0,0,0,0.06)',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <Animated.View style={{
              height: '100%',
              width: progressAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%']
              }),
              backgroundColor: progressPercent >= 75 ? colors.success : progressPercent >= 50 ? colors.primary : colors.warning,
              borderRadius: 4
            }} />
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
            <Text style={{ fontSize: 11, color: colors.textMuted }}>
              {dashboardStats.purchasedItems} of {dashboardStats.totalItems} items
            </Text>
            <Text style={{ fontSize: 11, color: colors.textMuted, fontWeight: '600' }}>
              {progressPercent}% complete
            </Text>
          </View>
        </View>
      )}

      {/* CTA Button with Hover Animation */}
      <Animated.View style={{ transform: [{ scale: buttonScale }] }}>
        <TouchableOpacity
          onPress={handleUpdateRegistry}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          activeOpacity={0.9}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            backgroundColor: isButtonHovered ? '#3d7a58' : colors.primary,
            paddingVertical: 14,
            borderRadius: 12,
            shadowColor: colors.primary,
            shadowOffset: { width: 0, height: isButtonHovered ? 8 : 4 },
            shadowOpacity: isButtonHovered ? 0.4 : 0.3,
            shadowRadius: isButtonHovered ? 12 : 8,
            elevation: isButtonHovered ? 6 : 4,
            ...(Platform.OS === 'web' ? { 
              cursor: 'pointer', 
              transition: 'background-color 0.2s ease, box-shadow 0.2s ease' 
            } : {})
          }}
        >
          <Ionicons name="add-circle-outline" size={18} color={colors.white} />
          <Text style={{ fontSize: 14, fontWeight: '600', color: colors.white }}>
            Update Registry
          </Text>
        </TouchableOpacity>
      </Animated.View>
    </Animated.View>
  );
};

export default RegistryStatistics;
